import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  DISC_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  generateContentKeyForAssessment,
  MODEL_TYPE_CAREER_PREFERENCE,
  MODEL_TYPE_EMOTIONAL_INTELLIGENCE,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";

import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import {
  getCookie,
  getEmotionalIntelligenceImage,
  getEnneagramBannerImage,
} from "../../utils/utility-functions";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import ExpandableSection from "../../components/expandable-collapsible/component";

import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const EmotionalIntelligenceReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();
  // const [content, setContent] = useState(null);
  const [contentKey, setContentKey] = useState("");
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, gender } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const bannerImage = getEnneagramBannerImage();
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);
  const { t } = useTranslation();

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }
        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_EMOTIONAL_INTELLIGENCE[currentLocale] ||
            MODEL_TYPE_EMOTIONAL_INTELLIGENCE[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });

        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const personalityGroup = currentAssessmentAttemptData?.personality?.role;
  const personalityName = currentAssessmentAttemptData?.personality?.name;
  const eiResult1Image = "ei-result-1";
  const eiResult2Image = "ei-result-2";

  const bannerImage1 = getEmotionalIntelligenceImage(eiResult1Image);
  const bannerImage2 = getEmotionalIntelligenceImage(eiResult2Image);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
    pink: "from-pink-600 via-pink-500",
  };

  const descriptiveCardColorMap = [
    { bg: "bg-blue-50", text: "text-blue-800" },
    { bg: "bg-purple-50", text: "text-purple-800" },
    { bg: "bg-amber-50", text: "text-amber-800" },
    { bg: "bg-green-50", text: "text-green-800" },
    { bg: "bg-pink-50", text: "text-pink-800" },
    { bg: "bg-sky-50", text: "text-sky-800" },
    { bg: "bg-orange-50", text: "text-orange-800" },
    { bg: "bg-emerald-50", text: "text-emerald-800" },
    { bg: "bg-violet-50", text: "text-violet-800" },
  ];

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("dashboard.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative w-full bg-gradient-to-r ${gradientClasses} to-purple-600 overflow-hidden`}
      >
        {/* Background Image */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover object-right pointer-events-none"
        />
        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Content */}
        <div className="relative z-10 px-4 py-8 sm:px-6 md:px-10">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 w-full  pr-10 md:pr-16">
            {/* Title Block */}
            <div className="w-full">
              <h2
                className="text-white font-bold text-2xl sm:text-3xl md:text-4xl break-words whitespace-pre-wrap w-full"
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
              >
                {title}
              </h2>
            </div>

            {/* Expandable Icon or Button */}
            {buttonNeeded && (
              <div className="sm:flex-shrink-0 mt-2 sm:mt-0">
                <TakeTestButton onClick={onClick} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ProgressCircle = ({
    color = "#805aed",
    duration = 2000,
    targetPercentage = 50,
  }) => {
    const [percentage, setPercentage] = useState(0);
    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const intervalTime = 30; // Time in milliseconds for each update

    useEffect(() => {
      const steps = duration / intervalTime; // Number of updates
      const stepSize = targetPercentage / steps; // Percentage to increase per step

      let currentPercentage = 0;
      const interval = setInterval(() => {
        currentPercentage += stepSize;
        if (currentPercentage >= targetPercentage) {
          currentPercentage = targetPercentage;
          clearInterval(interval);
        }
        setPercentage(parseInt(currentPercentage)); // Ensure whole numbers
      }, intervalTime);

      return () => clearInterval(interval); // Cleanup interval on unmount
    }, [duration, targetPercentage]);

    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="w-full max-w-xs p-4 mx-auto">
        <div className="relative pb-[100%] ease-in duration-100">
          <svg
            className="absolute top-0 left-0 w-full h-full transform -rotate-90"
            viewBox="0 0 120 120"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke="#E5E7EB"
              strokeWidth="10"
              fill="none"
            />
            {/* Animated Progress Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke={color}
              strokeWidth="10"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{
                transition: "stroke-dashoffset 0.1s ease-in-out",
              }}
            />
          </svg>

          {/* Percentage Text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-2xl font-bold text-gray-700">
            <span>{targetPercentage}%</span>
          </div>
        </div>
      </div>
    );
  };

  function getTraitEvaluation(traitObject, score) {
    if (traitObject && traitObject.Content) {
      traitObject.Content = traitObject.Content.replace(
        "{score}",
        score
      ).replace("Total Score", "");
    }

    let evaluationLevel;
    if (score >= 41 && score <= 50) {
      evaluationLevel = "High";
    } else if (score >= 26 && score <= 40) {
      evaluationLevel = "Moderate";
    } else if (score >= 10 && score <= 25) {
      evaluationLevel = "Low";
    } else {
      return { error: "Invalid score. Score must be between 10 and 50." };
    }

    return {
      RootHeading: traitObject?.RootHeading,
      totalScore: traitObject?.Content,
      Content: traitObject?.[evaluationLevel] || null,
    };
  }

  function getOverAllEvaluation(traitObject, score) {
    if (traitObject && traitObject.Content) {
      traitObject.Content = traitObject.Content.replace(
        "{score}",
        score
      ).replace("Total Score", "");
    }

    let evaluationLevel;
    if (score >= 200 && score <= 250) {
      evaluationLevel = "High";
    } else if (score >= 125 && score <= 199) {
      evaluationLevel = "Moderate";
    } else if (score >= 50 && score <= 124) {
      evaluationLevel = "Low";
    } else {
      return { error: "Invalid score. Score must be between 10 and 50." };
    }

    return {
      RootHeading: traitObject?.RootHeading,
      totalScore: traitObject?.Content,
      Content: traitObject?.[evaluationLevel] || null,
    };
  }

  function toCamelCase(str) {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  }

  function getTraitScoresWithTotal(userTraits) {
    const traits = userTraits.reduce((acc, { trait, score }) => {
      const key = toCamelCase(trait.title);
      acc[key] = score;
      return acc;
    }, {});

    const totalOverAllScore = Object.values(traits).reduce(
      (sum, score) => sum + score,
      0
    );

    return { traits, totalOverAllScore };
  }

  const empathyContent = getTraitEvaluation(
    contentData?.empathy,
    getTraitScoresWithTotal(userTraits).traits?.empathy
  );

  const motivationContent = getTraitEvaluation(
    contentData?.motivation,
    getTraitScoresWithTotal(userTraits).traits?.motivation
  );

  const overallScoreContent = getOverAllEvaluation(
    contentData?.overallScore,
    getTraitScoresWithTotal(userTraits).totalOverAllScore
  );

  const growingContent = getOverAllEvaluation(
    contentData?.growing,
    getTraitScoresWithTotal(userTraits).totalOverAllScore
  );

  const selfAwarenessContent = getTraitEvaluation(
    contentData?.selfAwareness,
    getTraitScoresWithTotal(userTraits).traits?.selfAwareness
  );

  const selfRegulationContent = getTraitEvaluation(
    contentData?.selfRegulation,
    getTraitScoresWithTotal(userTraits).traits?.selfRegulation
  );

  const socialSkillsContent = getTraitEvaluation(
    contentData?.socialSkills,
    getTraitScoresWithTotal(userTraits).traits?.socialSkills
  );

  const InfoListCard = ({ heading, bgClr, contentArray, overview }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full`}
      >
        {heading && (
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            {heading}
          </h2>
        )}
        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2">
            {contentArray?.map((item, index) => {
              const [title, ...descParts] = item.split(": ");
              const description = descParts.join(": ");

              return (
                <li key={index} className="flex gap-4">
                  <div className="w-8 h-8 flex">
                    <svg
                      width="100%"
                      height="100%"
                      viewBox="0 0 48 48"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <defs>
                        <filter
                          id="shadow"
                          x="-20%"
                          y="-20%"
                          width="140%"
                          height="140%"
                        >
                          <feDropShadow
                            dx="0"
                            dy="2"
                            stdDeviation="3"
                            floodColor="black"
                            floodOpacity="0.4"
                          />
                        </filter>
                      </defs>
                      <path
                        d="M24 33C28.9706 33 33 28.9706 33 24C33 19.0294 28.9706 15 24 15C19.0294 15 15 19.0294 15 24C15 28.9706 19.0294 33 24 33Z"
                        fill="#805fe6"
                        stroke="#fff"
                        strokeWidth="6"
                        filter="url(#shadow)"
                      />
                    </svg>
                  </div>

                  <p className="flex-1">
                    <strong className="text-[#805fe6]">{title}:</strong>{" "}
                    {description}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          filter={`url(#shadow-${character || "bullet"}-${actualSize})`}
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const colors = [
    "#eff4fd",
    "#f8f4fd",
    "#f2fcf9",
    "#fcf6ec",
    "#feeded",
    "#c7edf9",
    "#e7feeb",
    "#eff4fd",
    "#eae2fd",
  ];

  const CallToActionSection = ({ onClick1, onClick2 }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t(
                "report.emotional_intelligenc.learn_more_about_your_personality"
              )}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.emotional_intelligenc.discover_more_about_your")}
            </p>
            <TakeTestButton onClick={onClick1} />
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.emotional_intelligenc.ever_wondered_how_you_thoughts")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.emotional_intelligenc.discover_your_strengths")}
            </p>
            <TakeTestButton onClick={onClick2} />
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {token && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 ">
                      <div>
                        <p className="text-lg text-gray-700">
                          {contentData?.intro}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-3xl font-bold mb-2 text-center">
                          {t("report.emotional_intelligenc.overall_results")}:
                        </h4>
                        <ProgressCircle
                          targetPercentage={
                            (getTraitScoresWithTotal(userTraits)
                              ?.totalOverAllScore *
                              100) /
                            250
                          }
                        />
                      </div>
                      <div className="flex-1 min-h-96 flex items-center justify-center">
                        <img
                          src={bannerImage1}
                          alt="Banner Image 1"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        <>
                          {overallScoreContent?.RootHeading} {""}
                          {overallScoreContent?.totalScore}
                        </>
                      }
                      color="purple"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
                      <DescriptiveCard
                        heading={overallScoreContent?.Content?.Heading}
                        overview={overallScoreContent?.Content?.FlavorText}
                        bgClr={descriptiveCardColorMap[0].bg}
                        textClr={descriptiveCardColorMap[0].text}
                      />
                      {overallScoreContent?.Content?.Content.map(
                        (item, index) => (
                          <DescriptiveCard
                            heading={item?.Heading}
                            overview={item?.Content}
                            bgClr={
                              descriptiveCardColorMap[
                                (index + 1) % descriptiveCardColorMap.length
                              ].bg
                            }
                            textClr={
                              descriptiveCardColorMap[
                                (index + 1) % descriptiveCardColorMap.length
                              ].text
                            }
                          />
                        )
                      )}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        <>
                          {selfAwarenessContent?.RootHeading} {""}
                          {selfAwarenessContent?.totalScore}
                        </>
                      }
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
                      <DescriptiveCard
                        heading={selfAwarenessContent?.Content?.Heading}
                        overview={selfAwarenessContent?.Content?.FlavorText}
                        bgClr={descriptiveCardColorMap[0].bg}
                        textClr={descriptiveCardColorMap[0].text}
                      />

                      {selfAwarenessContent?.Content?.Content.map(
                        (item, index) => (
                          <DescriptiveCard
                            heading={item?.Heading}
                            overview={item?.Content}
                            bgClr={
                              descriptiveCardColorMap[
                                (index + 1) % descriptiveCardColorMap.length
                              ].bg
                            }
                            textClr={
                              descriptiveCardColorMap[
                                (index + 1) % descriptiveCardColorMap.length
                              ].text
                            }
                          />
                        )
                      )}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        <>
                          {selfRegulationContent?.RootHeading} {""}
                          {selfRegulationContent?.totalScore}
                        </>
                      }
                      color="green"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
                      <DescriptiveCard
                        heading={selfRegulationContent?.Content?.Heading}
                        overview={selfRegulationContent?.Content?.FlavorText}
                        bgClr={descriptiveCardColorMap[0].bg}
                        textClr={descriptiveCardColorMap[0].text}
                      />
                      {selfRegulationContent?.Content?.Content.map(
                        (item, index) => (
                          <DescriptiveCard
                            heading={item?.Heading}
                            overview={item?.Content}
                            bgClr={
                              descriptiveCardColorMap[
                                (index + 1) % descriptiveCardColorMap.length
                              ].bg
                            }
                            textClr={
                              descriptiveCardColorMap[
                                (index + 1) % descriptiveCardColorMap.length
                              ].text
                            }
                          />
                        )
                      )}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        <>
                          {motivationContent?.RootHeading} {""}
                          {motivationContent?.totalScore}
                        </>
                      }
                      color="indigo"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
                      <DescriptiveCard
                        heading={motivationContent?.Content?.Heading}
                        overview={motivationContent?.Content?.FlavorText}
                        bgClr={descriptiveCardColorMap[0].bg}
                        textClr={descriptiveCardColorMap[0].text}
                      />
                      {motivationContent?.Content?.Content.map(
                        (item, index) => (
                          <DescriptiveCard
                            heading={item?.Heading}
                            overview={item?.Content}
                            bgClr={
                              descriptiveCardColorMap[
                                (index + 1) % descriptiveCardColorMap.length
                              ].bg
                            }
                            textClr={
                              descriptiveCardColorMap[
                                (index + 1) % descriptiveCardColorMap.length
                              ].text
                            }
                          />
                        )
                      )}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        <>
                          {empathyContent?.RootHeading} {""}
                          {empathyContent?.totalScore}
                        </>
                      }
                      color="teal"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
                      <DescriptiveCard
                        heading={empathyContent?.Content?.Heading}
                        overview={empathyContent?.Content?.FlavorText}
                        bgClr={descriptiveCardColorMap[0].bg}
                        textClr={descriptiveCardColorMap[0].text}
                      />
                      {empathyContent?.Content?.Content.map((item, index) => (
                        <DescriptiveCard
                          heading={item?.Heading}
                          overview={item?.Content}
                          bgClr={
                            descriptiveCardColorMap[
                              (index + 1) % descriptiveCardColorMap.length
                            ].bg
                          }
                          textClr={
                            descriptiveCardColorMap[
                              (index + 1) % descriptiveCardColorMap.length
                            ].text
                          }
                        />
                      ))}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        <>
                          {socialSkillsContent?.RootHeading}{" "}
                          {socialSkillsContent?.totalScore}
                        </>
                      }
                      color="yellow"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
                      <DescriptiveCard
                        heading={socialSkillsContent?.Content?.Heading}
                        overview={socialSkillsContent?.Content?.FlavorText}
                        bgClr={descriptiveCardColorMap[0].bg}
                        textClr={descriptiveCardColorMap[0].text}
                      />
                      {socialSkillsContent?.Content?.Content.map(
                        (item, index) => (
                          <DescriptiveCard
                            heading={item?.Heading}
                            overview={item?.Content}
                            bgClr={
                              descriptiveCardColorMap[
                                (index + 1) % descriptiveCardColorMap.length
                              ].bg
                            }
                            textClr={
                              descriptiveCardColorMap[
                                (index + 1) % descriptiveCardColorMap.length
                              ].text
                            }
                          />
                        )
                      )}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={growingContent?.RootHeading}
                      color="pink"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div>
                        <p className="text-lg text-gray-700 my-8">
                          {contentData?.growing?.FlavorText}
                        </p>
                        <p className="text-lg text-gray-700 my-4 mt-12">
                          {contentData?.closing}
                        </p>
                      </div>
                      <div className="flex-1 h-64 flex items-center justify-center">
                        <img
                          src={bannerImage2}
                          alt={"Banner Image 2"}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {growingContent?.Content?.map((item, index) => (
                        <DescriptiveCard
                          heading={item?.Heading}
                          overview={item?.Content}
                          bgClr={
                            descriptiveCardColorMap[
                              index % descriptiveCardColorMap.length
                            ].bg
                          }
                          textClr={
                            descriptiveCardColorMap[
                              index % descriptiveCardColorMap.length
                            ].text
                          }
                        />
                      ))}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <CallToActionSection
                onClick1={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
                onClick2={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
              />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default EmotionalIntelligenceReport;
